import { isDefined } from "@clipboard-health/util-ts";
import { Link, Stack, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useSelectedWorkType } from "@src/appV2/redesign/Onboarding/hooks/useSelectedWorkType";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import { useIsHiringReferralEnabled } from "@src/appV2/redesign/Placements/useIsHiringReferralEnabled";
import { SHIFT_DISCOVERY_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import {
  Illustration,
  PageHeaderWrapper,
  Text,
  TextButton,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings, getTheme } from "@src/appV2/redesign/system/ui-theme";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { SupportContext } from "@src/appV2/support/constants";
import { ContactSupportButton } from "@src/appV2/support/ContactSupportButton";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useGetReferrals } from "../api/useGetReferrals";
import { useRedeemReferralCode } from "./api/useRedeemReferralCode";
import {
  HAS_SHOWN_REFERRAL_ACTIVE_MODAL_STORAGE_KEY,
  REFERRAL_CODE_QUERY_PARAM_KEY,
} from "./constants";
import { useRedeemReferralNavigation } from "./useRedeemReferralNavigation";
import { getErrorMessageFromRedeemResult, isReferralRedeemSuccess } from "./utils";

const SHIFT_DISCOVERY_PATH_V2 = `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_PATH}`;

export function ReferralRedeemCodePage() {
  const history = useHistory();
  const [, setHasShownReferralActiveModal] = useLocalStorage(
    HAS_SHOWN_REFERRAL_ACTIVE_MODAL_STORAGE_KEY,
    false
  );

  const { openArticle } = useZendeskArticle();
  const { workerReferralProgramSupportLink, howToContactSupportLink } = useSupportLinks();
  const { refetch: refetchReferrals } = useGetReferrals({});

  const { isPermanentPlacementsOnboarding, isFlexAndPermanentPlacementsOnboarding } =
    useSelectedWorkType();
  const isHiringReferralEnabled = useIsHiringReferralEnabled();

  const shouldRedirectToPlacements =
    (isPermanentPlacementsOnboarding || isFlexAndPermanentPlacementsOnboarding) &&
    isHiringReferralEnabled;

  const shouldAutoRedirect = !shouldRedirectToPlacements;

  const nextPath = shouldRedirectToPlacements ? PLACEMENT_PATHS.ROOT : SHIFT_DISCOVERY_PATH_V2;

  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const referralCode = queryParams.get(REFERRAL_CODE_QUERY_PARAM_KEY);
  const isReferredToHiring = queryParams.get("isReferredToHiring") === "true";

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_REFERRAL_REDEEM_PAGE, {
    referralCode,
    isHiringReferral: isReferredToHiring,
  });

  const {
    mutateAsync: redeemReferralCode,
    data: redeemResult,
    isLoading,
    isError,
  } = useRedeemReferralCode();

  const { clearReferralRedemption } = useRedeemReferralNavigation();

  useEffect(() => {
    async function handleRedeemCode() {
      if (isDefined(referralCode) && referralCode.length > 0) {
        await redeemReferralCode({
          referralCode,
          ...(isReferredToHiring && { isReferredToHiring }),
        });
        // So that the referral data shown in shift discovery page will be updated
        await refetchReferrals();
      }
    }

    // Automatically redeem the code if it exists in the URL
    handleRedeemCode().catch(() => {
      // Error is handled by component state (isError)
    });
  }, [referralCode, isReferredToHiring, redeemReferralCode, refetchReferrals]);

  const exitPage = useCallback(
    ({ nextPath, action }: { nextPath: string; action: string }) => {
      history.push(nextPath);
      clearReferralRedemption();
      logEvent(APP_V2_USER_EVENTS.REFERRAL_LINK_REDEEMED, {
        referralCodeValid: redeemResult?.referralCodeValid,
        bonusAmount: redeemResult?.bonusAmount,
        message: redeemResult?.message,
        referralCode,
        nextPath,
        action,
        isHiringReferral: isReferredToHiring,
      });
    },
    [history, clearReferralRedemption, redeemResult, referralCode, isReferredToHiring]
  );

  useEffect(() => {
    if (isReferralRedeemSuccess(redeemResult) && shouldAutoRedirect) {
      setHasShownReferralActiveModal(false);
      exitPage({ nextPath, action: "redirectOnSuccess" });
    }
  }, [redeemResult, setHasShownReferralActiveModal, exitPage, shouldAutoRedirect, nextPath]);

  const handleBrowseButtonClicked = () => {
    exitPage({ nextPath, action: "browseButtonClicked" });
  };

  const handleFaqClick = async () => {
    logEvent(APP_V2_USER_EVENTS.REFERRAL_REDEEM_FAQ_CLICKED);
    await openArticle(workerReferralProgramSupportLink);
  };

  const handleClose = () => {
    exitPage({ nextPath, action: "closeButtonClicked" });
  };

  function MessageContent({
    title,
    message,
    illustrationType,
    showFaqButton,
  }: {
    readonly title: string;
    readonly message: string;
    readonly illustrationType: "error" | "success" | "clipboard-bunny" | "pay";
    readonly showFaqButton?: boolean;
  }) {
    return (
      <Stack spacing={6} alignItems="center">
        <Illustration type={illustrationType} />
        <Stack spacing={6} alignItems="center">
          <Title component="h4" textAlign="center">
            {title}
          </Title>
          <Text light variant="body1" textAlign="center">
            {message}
          </Text>
        </Stack>
        {showFaqButton && (
          <TextButton LinkComponent={Link} onClick={handleFaqClick}>
            Frequently Asked Questions
          </TextButton>
        )}
      </Stack>
    );
  }

  const renderContent = () => {
    // Handle missing referral code first
    if (!isDefined(referralCode) || referralCode.length === 0) {
      return (
        <MessageContent
          showFaqButton
          title="Sorry, we encountered an issue redeeming your referral"
          message="Invalid referral link"
          illustrationType="error"
        />
      );
    }

    if (isError && !isLoading && !isDefined(redeemResult)) {
      return (
        <MessageContent
          title="Sorry, we encountered an issue redeeming your referral"
          message="Please try again later."
          illustrationType="error"
        />
      );
    }

    // Show loading while waiting for API response
    if (isLoading || !isDefined(redeemResult)) {
      return (
        <MessageContent
          title="Processing your referral..."
          message="Please wait while we apply your referral code"
          illustrationType="clipboard-bunny"
        />
      );
    }

    const { message, bonusAmount } = redeemResult;

    // Now we have an API response
    if (isReferralRedeemSuccess(redeemResult)) {
      if (!shouldAutoRedirect) {
        return (
          <MessageContent
            title={`Your $${bonusAmount} bonus awaits!`}
            message={message}
            illustrationType="pay"
          />
        );
      }

      // For successful redemption, we'll auto-redirect if shouldAutoRedirect is true
      return null;
    }

    const { title, message: errorMessage } = getErrorMessageFromRedeemResult(redeemResult);

    // Show error state only if we have an actual API error
    return (
      <MessageContent showFaqButton title={title} message={errorMessage} illustrationType="error" />
    );
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        variant="primary"
        sx={{
          ...getSafeAreaPaddings({ bottom: true }),
          paddingX: 2,
        }}
      >
        <PageHeaderWrapper>
          <PageHeaderTopRow
            hideBackButton
            rightAction={
              <IconButton iconType="close" size="large" variant="outlined" onClick={handleClose} />
            }
          />
        </PageHeaderWrapper>

        <PageContent>
          <Stack
            sx={{
              width: "100%",
              height: "100%",
              pb: 4,
            }}
            spacing={4}
          >
            <Stack flex={1} justifyContent="center">
              {renderContent()}
            </Stack>
            {!isLoading && (
              <Stack spacing={4} width="100%">
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleBrowseButtonClicked}
                >
                  {shouldRedirectToPlacements ? "Browse Jobs" : "Browse Shifts"}
                </Button>
                {(isError || !isReferralRedeemSuccess(redeemResult)) && (
                  <ContactSupportButton
                    supportContext={SupportContext.WORKER_REFERRALS}
                    supportLink={howToContactSupportLink}
                  >
                    Contact Support
                  </ContactSupportButton>
                )}
              </Stack>
            )}
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
